import { useMemo } from 'react';
import { useContent } from 'src/contexts/ContentContext';
import { useEventMapContext } from 'src/contexts/EventMapContext';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import { ContentId } from 'src/utils/constants/contentId';
import { getQuantityNumberFormat } from 'src/utils/format';

import { useMobileListingDrawerContext } from '../MobileListingDrawerContext';
import * as styles from './MobileListingSectionSummary.css';

export const MobileListingSectionSummary = () => {
  const {
    selectedListing,
    pricingSettings,
    eventListings,
    listingPriceCalculation,
  } = useMobileListingDrawerContext();
  const listingsText = useContent(ContentId.Listings);
  const listingText = useContent(ContentId.Listing);
  const availableText = useContent(ContentId.Available);
  const { venueMapInfo } = useEventMapContext();

  const sectionToSectionId = useMemo(() => {
    return (venueMapInfo?.sections ?? []).reduce(
      (acc, section) => {
        acc[section.name] = section.id;
        return acc;
      },
      {} as Record<string, number>
    );
  }, [venueMapInfo?.sections]);

  const [ownListingCount, allListingCount] = useMemo(() => {
    const selectedSections =
      pricingSettings?.compListingSelectedSectionSettings?.sectionIdFilter ??
      [];
    const qualifiedOwnListings = (eventListings ?? []).filter(
      ({ seating }) =>
        (seating.sectionId && selectedSections.includes(seating.sectionId)) ||
        seating.section === selectedListing?.seating?.section
    );

    const qualifiedAllListings = (
      listingPriceCalculation?.compListings ?? []
    ).filter(({ section }) => {
      if (!section) {
        return false;
      }
      const sectionId = sectionToSectionId[section];
      return selectedSections.includes(sectionId);
    });

    return [qualifiedOwnListings.length, qualifiedAllListings.length];
  }, [
    pricingSettings?.compListingSelectedSectionSettings?.sectionIdFilter,
    eventListings,
    listingPriceCalculation?.compListings,
    selectedListing?.seating?.section,
    sectionToSectionId,
  ]);

  const listingsLabel = useMemo(() => {
    if (ownListingCount <= 1) {
      return listingText;
    }
    return listingsText;
  }, [listingText, listingsText, ownListingCount]);

  return (
    <Stack
      width="full"
      alignItems="center"
      justifyContent="spaceBetween"
      style={{ padding: `0 ${vars.spacing.sm}`, color: vars.color.textPrimary }}
    >
      <span>
        {getQuantityNumberFormat().format(ownListingCount)} {listingsLabel}
      </span>
      <span className={styles.subtitle}>
        {getQuantityNumberFormat().format(allListingCount)} {availableText}
      </span>
    </Stack>
  );
};
